/**
 * Grid trading strategy — types and configuration.
 *
 * ATR-based grid that profits from ranging-market volatility on BTC + ETH.
 * Runs as a parallel mode alongside the directional signal-based strategy
 * with isolated capital allocation.
 */

// ── Types ──

export interface GridLevel {
  /** Price at which this order fills. */
  price: number;
  /** 'buy' below current price, 'sell' above. */
  side: 'buy' | 'sell';
  /** Quantity per fill (in token units, e.g. 0.0124 BTC). */
  quantity: number;
  /** True if this level has been filled and is waiting for its paired order. */
  filled: boolean;
  /** Timestamp of fill (0 if unfilled). */
  filledAt: number;
}

export interface GridFill {
  /** Token ID (e.g. 'bitcoin'). */
  token: string;
  /** Price at which we bought. */
  buyPrice: number;
  /** Price at which the paired sell should execute. */
  targetSellPrice: number;
  /** Quantity in token units. */
  quantity: number;
  /** Timestamp of buy fill. */
  filledAt: number;
  /** If this fill has been closed by its paired sell. */
  closed: boolean;
  /** PnL when closed (before leverage). */
  pnlUsd: number;
  /** Timestamp of close. */
  closedAt: number;
}

export interface GridStats {
  /** Completed buy→sell round-trips. */
  totalRoundTrips: number;
  /** Cumulative grid profit in USD (after leverage). */
  totalPnlUsd: number;
  /** Today's grid profit (resets UTC midnight). */
  todayPnlUsd: number;
  /** Total fills (buy + sell) since inception. */
  totalFills: number;
  /** Today's fills. */
  todayFills: number;
  /** Last daily reset timestamp. */
  lastDailyReset: number;
  /** Last full grid rebuild timestamp. */
  lastRebalanceAt: number;
}

export interface GridTokenState {
  /** CoinGecko token ID. */
  token: string;
  /** Current active grid levels. */
  levels: GridLevel[];
  /** Open fills waiting for their paired sell/buy. */
  openFills: GridFill[];
  /** USD allocated to this token's grid. */
  allocation: number;
  /** Cumulative stats for this token's grid. */
  stats: GridStats;
  /** Price at center when grid was last built/rebalanced. */
  centerPrice: number;
  /** ATR at last build. */
  atr: number;
  /** Trend at last grid build: (latest_4h_close - lookback_first_4h_close) / lookback_first_4h_close. */
  trend: number;
  /** Last time grid.trend was refreshed mid-tick (separate from buildGrid). */
  lastTrendRefreshAt: number;
}

export interface GridPortfolioState {
  /** Total USD carved out for the grid strategy. */
  totalAllocation: number;
  /** Per-token grid state. */
  grids: GridTokenState[];
  /** Whether grid is paused (pool dropped below threshold). */
  paused: boolean;
  /** Reason for pause (empty if active). */
  pauseReason: string;
  /** Timestamp of initial allocation. */
  initializedAt: number;
  /** Hard circuit breaker tripped — pool drawdown exceeded
   *  `circuitBreakerThresholdPct`. Auto-resume is BLOCKED while this is set;
   *  only an explicit `sherwood grid resume` clears it. Persists in
   *  portfolio.json so a process restart does not silently re-engage. */
  circuitBroken?: boolean;
  /** Timestamp when the circuit breaker was tripped (0 / undefined = never). */
  circuitBrokenAt?: number;
}

// ── Configuration ──

export interface GridConfig {
  /** Enable/disable the grid strategy. */
  enabled: boolean;
  /** Tokens to run grids on. */
  tokens: string[];
  /** Fraction of total portfolio to allocate to grid (0.35 = 35%). */
  allocationPct: number;
  /** Leverage multiplier on grid capital. */
  leverage: number;
  /** Number of buy levels below price (same number of sell levels above). */
  levelsPerSide: number;
  /** Grid range = price ± (atrMultiplier × ATR). */
  atrMultiplier: number;
  /** ATR lookback period. */
  atrPeriod: number;
  /** Shift grid when price drifts past this fraction of the range toward one edge. */
  rebalanceDriftPct: number;
  /** Full grid rebuild interval in ms. */
  fullRebuildIntervalMs: number;
  /** Per-token allocation split (must sum to 1.0). */
  tokenSplit: Record<string, number>;
  /** Skip fills where profit per round-trip < this (fee floor). */
  minProfitPerFillUsd: number;
  /** Pause grid if pool drops this fraction from initial allocation. */
  pauseThresholdPct: number;
  /** Resume paused grid when drawdown recovers below this fraction (hysteresis). */
  unpauseRecoveryPct: number;
  /** Hard circuit-breaker drawdown threshold. When pool MtM drop hits this,
   *  the grid is permanently paused (auto-resume disabled) until an operator
   *  runs `sherwood grid resume`. Distinct from `pauseThresholdPct` — the
   *  soft pause has hysteresis and can re-engage on its own; the circuit
   *  breaker requires manual review. Default 0.30 (30% drawdown). */
  circuitBreakerThresholdPct: number;
  /** Per-token maintenance margin fraction. Backtester uses this to model
   *  exchange liquidation. Default 0.02 = 2% (typical Hyperliquid). */
  maintenanceMarginPct: number;
  /** Max open notional per token as a multiple of (allocation × leverage).
   *  When exceeded, new buy fills are refused (existing fills can still
   *  close as sells). Caps unbounded accumulation across rebuilds in
   *  downtrends. Default 2.0 (allows up to 2 grid generations). */
  maxOpenNotionalMultiple: number;
  /** Block new buy fills when trend over the ATR lookback is below this
   *  threshold. Default 0.10 (10% drop in 56h = clear downtrend → don't
   *  buy into the falling knife). Set to 0 to disable filter. */
  downtrendBlockPct: number;
  /** Force-close any open buy if currentPrice drops this fraction below
   *  buyPrice. Per-position stop-loss: limits how much any single fill can
   *  contribute to the cross-margin pool's drawdown. Default 0.10 (10%
   *  adverse from entry — empirical Nov–May: 10% saves the strategy at -4%,
   *  30% lets liquidations through at -37%). 0 = disabled. */
  stopLossPct: number;
}

export const DEFAULT_GRID_CONFIG: GridConfig = {
  enabled: true,
  tokens: ['bitcoin', 'ethereum', 'solana'],
  allocationPct: 0.50,
  leverage: 5,                                  // was 4 — +25% profit per RT
  levelsPerSide: 12,                            // disjoint-window winner — lowest MaxDD (18% vs 21-22%) at modest avg PnL trade-off
  atrMultiplier: 3,                             // ATR×3 dominates ATR×2 across all tested windows (lower fee burn)
  atrPeriod: 14,
  rebalanceDriftPct: 0.40,                      // was 0.55 — rebalance faster, keep levels near price
  fullRebuildIntervalMs: 12 * 60 * 60 * 1000,   // 12h
  tokenSplit: { bitcoin: 0.55, ethereum: 0.25, solana: 0.20 },  // BTC-tilt — 30d backtest 2026-04-18..05-18 vs prior 0.45/0.30/0.25: net +$464 (+9.28%) vs +$422 (+8.44%) and max DD -$546 vs -$598
  minProfitPerFillUsd: 0.50,
  pauseThresholdPct: 0.20,                      // post leverage-fix calibration: fires at ~4% adverse move on 5x (real dollar terms); old 0.40 required ~8% which essentially never fired before liquidation
  unpauseRecoveryPct: 0.05,                     // hysteresis: resume when pool recovers to within 5% of initial (half of pause threshold to avoid ping-pong)
  circuitBreakerThresholdPct: 0.30,             // hard halt at 30% drawdown — manual resume required. Bear-sweep: BEAR-60d went from -20% to -53% in second month; halting at -30% saves the rest.
  maintenanceMarginPct: 0.02,
  maxOpenNotionalMultiple: 2.0,
  downtrendBlockPct: 0.10,
  stopLossPct: 0.10,                            // empirical Nov-May: 10% saves the strategy (-4%); 30% lets liquidations through (-37%)
};
